import React, { FormEvent, useState } from "react";
import { Box, TextField, Typography, InputAdornment, IconButton } from "@mui/material";
import { ReactComponent as LogoFilledFull } from "./Icons/LogoFilledFull.svg";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../redux/slices/notificationSlice";
import { useSignin } from "./auth/useSignIn";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SocialBar from "./SocialBar";
import useEnhancedNavigate from "./Session/helpers";
import { formattedNotifMessage } from "../helpers";
import ResetPassword from "./auth/ResetPassword";
import Footer from "./Plans/Footer";
import LoadingButton from "./Shared/LoadingButton";
import { validateSignInForm } from "./auth/helpers";
import { AuthActions } from "./Sidebar/constants";
import { closeAuthModal, openAuthModal } from "../redux/slices/userSlice";

const SigninPage = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const signin = useSignin();
  const dispatch = useDispatch();
  const navigate = useEnhancedNavigate();

  const { authModal } = useSelector((state: { user }) => state.user);

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleSignUp = () => {
    if (authModal.type) {
      dispatch(openAuthModal({ type: AuthActions.SIGNUP }));
    }
    else {
      navigate("/signup");
    }
  };

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault();
    const validation = validateSignInForm(email, showPasswordField ? password : "");
    setEmailError(validation.errors.email);
    if (!validation.errors.email && !showPasswordField) {
      setShowPasswordField(true);
      return;
    }
    else {
      setPasswordError(validation.errors.password);
    }
    if (!validation.valid) return;
    try {
      setIsLoading(true);

      await signin(email, password, () => { }, true);

      dispatch(showNotification({ message: "Sign In Successful!", severity: "success", horizontal: "right" }));
    }
    catch (error) {
      const formattedMessage = formattedNotifMessage(error.code);
      dispatch(showNotification({ message: formattedMessage, severity: "error", horizontal: "right" }));
    }
    finally {
      setIsLoading(false);
      dispatch(closeAuthModal());
    }
  };
  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <div className="w-full h-full flex flex-col justify-between p-4 md:p-12 bg-white dark:bg-background/paper-elevation-1 text-mui-black-87 dark:text-white overflow-auto">
      <div className="flex items-center justify-center pb-6 cursor-pointer" onClick={handleLogoClick}>
        <LogoFilledFull className="text-black dark:text-white w-8 h-[34px]" />
        <h2 className="text-xl font-semibold ml-3 text-black dark:text-white">TradeGPT</h2>
      </div>

      <div className="flex-grow">
        <section className="flex justify-center items-center h-full">
          {isForgotPassword
            ? <ResetPassword setIsForgotPassword={setIsForgotPassword} />
            : (
                <Box className="!h-auto">
                  <div className="flex flex-col justify-center items-center">
                    <Typography variant="h4/regular-400" className="!text-[24px] md:!text-[34px]">
                      Welcome Back
                    </Typography>
                  </div>
                  <form onSubmit={handleContinue} className="pt-8 flex flex-col justify-start items-center gap-y-4 md:gap-y-6">
                    <TextField
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      label="Email address"
                      variant="outlined"
                      className="w-[300px] md:w-[340px] outlined-input labelText"
                      error={!!emailError}
                      helperText={emailError}
                    />
                    {showPasswordField && (
                      <div className="flex flex-col">
                        <TextField
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOff className="!fill-action-active" /> : <Visibility className="!fill-action-active" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          label="Password"
                          type={showPassword ? "text" : "password"}
                          variant="outlined"
                          className="w-[300px] md:w-[340px] mb-4 outlined-input labelText autofill-transparent"
                          error={!!passwordError}
                          helperText={passwordError}
                          inputProps={{
                            className: "autofill-transparent",
                          }}
                        />
                        <Typography variant="body1/regular-400" className="text-tgpt-secondary-main text-left pt-4 pb-2 cursor-pointer hover:text-tgpt-secondary-dark" onClick={() => setIsForgotPassword(true)}>Forgot password?</Typography>
                      </div>
                    )}
                    <LoadingButton
                      variant="contained"
                      className="h-[50px] text-white text-[15px] font-medium leading-[26px] !bg-tgpt-secondary-main !normal-case disabled:!bg-action-disabled-background disabled:!text-action-disabled"
                      isDisabled={!email}
                      fullWidth
                      isLoading={isLoading}
                      onClick={handleContinue}
                      type="submit"
                    >
                      Continue
                    </LoadingButton>

                    <Typography variant="body1/regular-400" className="text-center !pb-0 lg:pb-4">
                      Don&apos;t have an account?
                      {" "}
                      <span onClick={handleSignUp} className="cursor-pointer text-primary-blue-dark hover:underline">
                        Sign up now!
                      </span>
                    </Typography>
                    {!showPasswordField && <SocialBar />}
                  </form>
                </Box>
              )}
        </section>
      </div>

      <Footer showLogo={false} />
    </div>
  );
};

export default SigninPage;
